// AlgorithmDocument — renders one full algorithm as a scrolled document with
// flowTrack / branches / quad / panel / disclaimer block types.

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

const ACCENT = {
  teal: { border: "border-teal-700/50", bg: "bg-teal-700/5", text: "text-teal-700", badge: "bg-teal-700" },
  sky: { border: "border-sky-700/50", bg: "bg-sky-700/5", text: "text-sky-700", badge: "bg-sky-700" },
  purple: { border: "border-purple-700/50", bg: "bg-purple-700/5", text: "text-purple-700", badge: "bg-purple-700" },
  amber: { border: "border-amber-700/50", bg: "bg-amber-700/5", text: "text-amber-700", badge: "bg-amber-700" },
  stone: { border: "border-stone-400", bg: "bg-stone-100", text: "text-stone-800", badge: "bg-stone-700" },
  slate: { border: "border-stone-400", bg: "bg-stone-100/70", text: "text-stone-600", badge: "bg-stone-500" },
  danger: { border: "border-red-600/60", bg: "bg-red-600/5", text: "text-red-700", badge: "bg-red-600" },
  primary: null, // resolved to the specialty color
};

function pickAccent(color, key) {
  if (key === "primary") return ACCENT[color] || ACCENT.sky;
  return ACCENT[key] || ACCENT.sky;
}

function pick(txt, lang) {
  if (!txt) return "";
  if (typeof txt === "string") return txt;
  return txt[lang] || txt.en || txt.id || "";
}

function Step({ step, lang, accent }) {
  return (
    <div className="flex gap-3">
      <div
        className={cn(
          "shrink-0 h-8 w-8 rounded-full text-white text-xs font-mono font-bold flex items-center justify-center",
          accent.badge
        )}
      >
        {step.badge}
      </div>
      <div
        className={cn(
          "flex-1 border rounded-sm p-4 bg-white",
          step.decision ? "border-amber-700/50 bg-amber-700/5" : accent.border
        )}
      >
        {step.eyebrow && pick(step.eyebrow, lang) && (
          <div className={cn("text-[10px] tracking-[0.18em] uppercase font-semibold mb-1", step.decision ? "text-amber-700" : accent.text)}>
            {pick(step.eyebrow, lang)}
          </div>
        )}
        {pick(step.body, lang) && (
          <p className="text-sm text-stone-800 leading-relaxed">{pick(step.body, lang)}</p>
        )}
        {step.list && (
          <ul className="mt-2 space-y-1.5 pl-4 list-disc marker:text-stone-400">
            {(step.list[lang] || step.list.en || []).map((item, i) => (
              <li key={i} className="text-sm text-stone-700 leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        )}
        {step.bodyExtra && pick(step.bodyExtra, lang) && (
          <p className="mt-2 text-sm text-stone-700 leading-relaxed">{pick(step.bodyExtra, lang)}</p>
        )}
        {step.danger && pick(step.danger, lang) && (
          <p className="mt-2 text-sm text-red-700 leading-relaxed font-medium">{pick(step.danger, lang)}</p>
        )}
      </div>
    </div>
  );
}

function FlowTrack({ block, lang, primaryAccent }) {
  return (
    <div className="space-y-3">
      {block.steps.map((s, i) => (
        <Step key={i} step={s} lang={lang} accent={primaryAccent} />
      ))}
    </div>
  );
}

function BranchCol({ side, branch, lang, primaryAccent, color }) {
  const accent = pickAccent(color, branch.color || "primary");
  return (
    <div className="flex-1">
      <h4 className={cn("text-sm font-semibold mb-3 pb-2 border-b", accent.text, accent.border)} data-testid={`branch-${side}`}>
        {pick(branch.heading, lang)}
      </h4>
      <div className="space-y-3">
        {branch.steps.map((s, i) => (
          <Step key={i} step={s} lang={lang} accent={accent} />
        ))}
      </div>
      {branch.loop && (
        <div className={cn("mt-3 text-xs italic", accent.text)}>{pick(branch.loop, lang)}</div>
      )}
    </div>
  );
}

function Branches({ block, lang, primaryAccent, color }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
      <BranchCol side="left" branch={block.left} lang={lang} primaryAccent={primaryAccent} color={color} />
      <BranchCol side="right" branch={block.right} lang={lang} primaryAccent={primaryAccent} color={color} />
    </div>
  );
}

function Quad({ block, lang }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {block.cells.map((cell, i) => {
        const accent = pickAccent("sky", cell.accent || "sky");
        return (
          <div key={i} className={cn("border rounded-sm p-4", accent.border, accent.bg)}>
            <div className={cn("font-semibold text-sm", accent.text)}>{pick(cell.title, lang)}</div>
            <div className="text-xs text-stone-700 mt-1 leading-relaxed">{pick(cell.desc, lang)}</div>
          </div>
        );
      })}
    </div>
  );
}

function Panel({ block, lang }) {
  return (
    <section className="border border-stone-200 bg-white rounded-sm p-5">
      <h3 className="font-display text-base font-medium text-stone-900 mb-3">{pick(block.heading, lang)}</h3>
      {block.paragraph && (
        <p className="text-sm text-stone-700 leading-relaxed">{pick(block.paragraph, lang)}</p>
      )}
      {block.list && (
        <ol className="space-y-1.5 pl-5 list-decimal marker:font-mono marker:text-stone-400 marker:text-xs">
          {(block.list[lang] || block.list.en || []).map((item, i) => (
            <li key={i} className="text-sm text-stone-800 leading-relaxed">
              {item}
            </li>
          ))}
        </ol>
      )}
      {block.tags && (
        <div className="flex flex-wrap gap-2">
          {block.tags.map((tag, i) => (
            <span
              key={i}
              className="inline-flex items-center border border-stone-300 bg-stone-50 text-stone-800 text-xs font-mono rounded-sm px-2 py-1"
            >
              {pick(tag, lang)}
            </span>
          ))}
        </div>
      )}
      {block.table && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                {(block.table.headers[lang] || block.table.headers.en).map((h, i) => (
                  <th
                    key={i}
                    className="text-left font-semibold text-stone-700 text-[11px] tracking-wider uppercase border-b border-stone-300 py-2 pr-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.table.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-stone-100">
                  {row.map((cell, ci) => (
                    <td key={ci} className="py-2 pr-3 text-stone-800 align-top">
                      {pick(cell, lang)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {block.footnote && (
        <p className="mt-3 text-xs text-stone-500 leading-relaxed">{pick(block.footnote, lang)}</p>
      )}
    </section>
  );
}

function Disclaimer({ block, lang }) {
  return (
    <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed max-w-2xl">
      {pick(block.text, lang)}
    </div>
  );
}

// Inline placeholder SVG rendered when `src` is empty — swap `src` in the data
// file with a real ECG image URL to replace.
function EcgPlaceholder({ rhythm }) {
  // "vt" = wide monomorphic, "vf" = chaotic. Different SVG path per rhythm.
  const path =
    rhythm === "vf"
      ? "M0 40 L20 42 L30 15 L38 65 L46 25 L52 55 L60 20 L68 60 L76 30 L84 50 L92 22 L100 58 L108 28 L116 55 L124 25 L132 60 L140 30 L148 50 L156 20 L164 62 L172 32 L180 55 L188 25 L196 58 L204 30 L212 50 L220 22 L228 60 L236 30 L244 55 L252 25 L260 58 L268 30 L276 50 L284 22 L292 60 L300 40"
      : "M0 40 L20 40 L28 10 L36 70 L44 40 L64 40 L72 10 L80 70 L88 40 L108 40 L116 10 L124 70 L132 40 L152 40 L160 10 L168 70 L176 40 L196 40 L204 10 L212 70 L220 40 L240 40 L248 10 L256 70 L264 40 L284 40 L292 10 L300 40";
  return (
    <svg viewBox="0 0 300 80" className="w-full h-24 bg-white" role="img" aria-hidden="true">
      <defs>
        <pattern id="ecg-grid" width="10" height="10" patternUnits="userSpaceOnUse">
          <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#fecaca" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="300" height="80" fill="url(#ecg-grid)" />
      <path d={path} fill="none" stroke="#1f2937" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ImageGrid({ block, lang }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ecg-image-grid">
      {block.images.map((img, i) => (
        <figure
          key={i}
          className="border border-stone-200 bg-white rounded-sm overflow-hidden"
          data-testid={`ecg-tile-${img.rhythm || i}`}
        >
          <div className="bg-stone-50 border-b border-stone-200 flex items-center justify-center">
            {img.src ? (
              // Real ECG image once user replaces `src` in the data file.
              <img
                src={img.src}
                alt={pick(img.alt, lang)}
                className="w-full h-24 object-cover"
                data-testid={`ecg-img-${img.rhythm || i}`}
              />
            ) : (
              <EcgPlaceholder rhythm={img.rhythm} />
            )}
          </div>
          <figcaption className="px-3 py-2 text-xs font-mono text-stone-700 tracking-wide flex items-center justify-between">
            <span>{pick(img.caption, lang)}</span>
            {!img.src && (
              <span className="text-[10px] uppercase tracking-widest text-stone-400">Placeholder</span>
            )}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

function LinkButton({ block, lang }) {
  return (
    <Link
      to={block.to}
      data-testid={`algo-linkbutton-${(block.to || "").replace(/\W+/g, "-")}`}
      className="group flex items-center justify-between gap-4 border border-sky-700/40 bg-sky-700/5 hover:bg-sky-700/10 hover:border-sky-700 rounded-sm px-5 py-4 transition-colors"
    >
      <div>
        <div className="text-sm md:text-base font-semibold text-sky-800">{pick(block.label, lang)}</div>
        {block.description && (
          <div className="text-xs text-stone-600 mt-0.5 leading-relaxed">{pick(block.description, lang)}</div>
        )}
      </div>
      <span className="shrink-0 h-8 w-8 rounded-full bg-sky-700 text-white flex items-center justify-center group-hover:bg-sky-800 transition-colors">
        <ArrowUpRight size={16} strokeWidth={1.75} />
      </span>
    </Link>
  );
}

export default function AlgorithmDocument({ algorithm, specialty, backTo }) {
  const { lang, t } = useLanguage();
  const primaryAccent = ACCENT[specialty.color] || ACCENT.sky;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-10 py-8 md:py-12" data-testid={`algorithm-doc-${algorithm.id}`}>
      <div className="mb-6">
        <Link
          to={backTo}
          data-testid="algo-doc-back"
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase text-stone-500 font-mono hover:text-sky-700 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          {t("algo.back_to_specialty").replace("{name}", pick(specialty.name, lang))}
        </Link>
      </div>

      <header className="border-b border-stone-200 pb-5 mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {pick(algorithm.title, lang)}
        </h1>
        <p className="mt-3 text-xs md:text-sm text-stone-500 leading-relaxed">{pick(algorithm.meta, lang)}</p>
      </header>

      <div className="space-y-8">
        {algorithm.blocks.map((block, i) => {
          switch (block.type) {
            case "flowTrack":
              return <FlowTrack key={i} block={block} lang={lang} primaryAccent={primaryAccent} />;
            case "branches":
              return <Branches key={i} block={block} lang={lang} primaryAccent={primaryAccent} color={specialty.color} />;
            case "quad":
              return <Quad key={i} block={block} lang={lang} />;
            case "panel":
              return <Panel key={i} block={block} lang={lang} />;
            case "imageGrid":
              return <ImageGrid key={i} block={block} lang={lang} />;
            case "linkButton":
              return <LinkButton key={i} block={block} lang={lang} />;
            case "disclaimer":
              return <Disclaimer key={i} block={block} lang={lang} />;
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
}
